// $FlowFixMe
import React, { Fragment, useMemo } from 'react';
import { reduxForm, Form, Field } from 'redux-form';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Grid, Row, Col } from 'react-bootstrap';
import {
  renderNumberField,
  renderTextField,
  renderSelectField,
  renderCurrencyField,
} from '../../../../../utils/redux-form-helper';
import { Tooltip } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AlertInfoIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import usePermissionsHook from '../../../../../components/ElementWithPermissions/usePermissionsHook';

import Spinner from '../../../../../components/Spinner';
import { FORM_NAME } from '../../constants';
import messages from '../messages';
import { useAsyncStateOptions } from '../hooks';
import validate from './validate';

type ReduxFormProps = {
  handleSubmit: Function,
};

type Props = ReduxFormProps & {
  onSubmit: Function,
  initialValues: Object,
  handleDeleteVehicle: Function,
  showXmlUnitNumberField: boolean,
  isPHFloorPlan: boolean,
  isHUDFloorPlan: boolean,
  intl: Object,
};

const ADA_OPTIONS = [
  {
    value: 'true',
    text: 'Yes',
  },
  {
    value: 'false',
    text: 'No',
  },
];

const GeneralUnitInformationForm = ({
  handleSubmit,
  initialValues,
  onSubmit,
  showXmlUnitNumberField,
  isPHFloorPlan,
  isHUDFloorPlan,
  flags,
  intl,
}: Props) => {
  const unitOptions = useAsyncStateOptions();
  const featureFlags = useFlags();
  const hasUnitPricingEditPermission = usePermissionsHook({
    scope: ['unit-pricing-edit'],
  });

  const { removeUnitFloorLevelRestrictions } = flags;

  // Show Unit Price field if feature flag is enabled (regardless of availability)
  const showUnitPriceField = featureFlags?.unitLevelPrice;

  // Disable field if unit is not available OR if user doesn't have permission (memoized)
  const disableUnitPriceField = useMemo(
    () =>
      !initialValues?.unitStatus?.isAvailable ||
      (featureFlags?.unitLevelPrice && !hasUnitPricingEditPermission),
    [
      initialValues?.unitStatus?.isAvailable,
      featureFlags?.unitLevelPrice,
      hasUnitPricingEditPermission,
    ],
  );

  return (
    <Fragment>
      {!unitOptions ? (
        <Spinner />
      ) : (
        <Form
          className="form-container container-fluid"
          onSubmit={handleSubmit}
        >
          <Grid fluid>
            <Row>
              <Col xs={12} sm={6}>
                <FormattedMessage
                  {...(removeUnitFloorLevelRestrictions
                    ? messages.unitFloorLevel
                    : messages.unitLevel)}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Field
                  name="unitFloorLevel"
                  component={
                    removeUnitFloorLevelRestrictions
                      ? renderTextField
                      : renderNumberField
                  }
                  label="Unit Level"
                  className="input form-control"
                  maxLength={removeUnitFloorLevelRestrictions ? undefined : 2}
                  max={25}
                  min={0}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={6}>
                <FormattedMessage {...messages.unitAddress} />
              </Col>
              <Col xs={12} sm={6}>
                <Field
                  name="streetAddress1"
                  component={renderTextField}
                  label="Street Address 1"
                  placeholder="Address Line 1"
                  className="input form-control"
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={6} />
              <Col xs={12} sm={6}>
                <Field
                  name="streetAddress2"
                  component={renderTextField}
                  label="Street Address 2"
                  placeholder="Address Line 2"
                  className="input form-control"
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={6} />
              <Col xs={12} sm={6}>
                <Field
                  name="city"
                  component={renderTextField}
                  label="City"
                  placeholder="City"
                  className="input form-control"
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={6} />
              <Col xs={6} sm={3}>
                <Field
                  name="state"
                  component={renderSelectField}
                  className="input form-control no-border no-padding"
                  options={unitOptions.stateOptions}
                />
              </Col>
              <Col xs={6} sm={3}>
                <Field
                  name="zipCode"
                  component={renderTextField}
                  label="Zip Code / Postal Code"
                  placeholder="Zip Code / Postal Code"
                  className="input form-control"
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} sm={6}>
                <FormattedMessage {...messages.unitADA} />
              </Col>
              <Col xs={6} sm={6}>
                <Field
                  name="isADA"
                  component={renderSelectField}
                  className="input form-control no-border no-padding"
                  options={ADA_OPTIONS}
                />
              </Col>
            </Row>
            {showXmlUnitNumberField && (
              <Row>
                <Col xs={12} sm={6}>
                  <FormattedMessage {...messages.altXMLUnitNumber} />
                </Col>
                <Col xs={12} sm={6}>
                  <Field
                    name="xmlUnitNumber"
                    component={renderTextField}
                    label="Alternative Unit Name (XML)"
                    placeholder="Alternative Unit Name (XML)"
                    className="input form-control"
                    maxLength={255}
                  />
                </Col>
              </Row>
            )}
            {showUnitPriceField && (
              <Row>
                <Col xs={12} sm={6}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <FormattedMessage {...messages.unitPrice} />
                    <Tooltip
                      title={
                        <FormattedMessage {...messages.unitPriceTooltip} />
                      }
                      placement="top"
                      arrow
                      style={{
                        display: 'inline-flex',
                        marginLeft: '8px',
                        verticalAlign: 'middle',
                      }}
                    >
                      <AlertInfoIcon fontSize="small" />
                    </Tooltip>
                  </div>
                </Col>
                <Col xs={12} sm={6}>
                  <Field
                    name="quotingRentAmount"
                    component={renderCurrencyField}
                    placeholder="0.00"
                    className="input form-control"
                    step="0.01"
                    disabled={disableUnitPriceField}
                  />
                </Col>
              </Row>
            )}
            {isPHFloorPlan && !featureFlags?.affUnitDetails && (
              <Row>
                <Col xs={12} sm={6}>
                  <FormattedMessage {...messages.phUnitNumber} />
                </Col>
                <Col xs={12} sm={6}>
                  <Field
                    name="PHUnitNumber"
                    component={renderTextField}
                    label={intl.formatMessage(messages.phUnitNumber)}
                    placeholder={intl.formatMessage(messages.phUnitNumber)}
                    className="input form-control"
                    maxLength={10}
                  />
                </Col>
              </Row>
            )}
          </Grid>
        </Form>
      )}
    </Fragment>
  );
};

export default injectIntl(
  reduxForm({
    form: FORM_NAME,
    validate,
    touchOnChange: true,
  })(GeneralUnitInformationForm),
);
